import { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import {
  Brain,
  Sparkles,
  Loader2,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Target,
  Lightbulb,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  MessageSquare
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AI Autopilot Panel - Groq-Powered Deal Intelligence (Llama 3.3 70B)
 * 
 * Features:
 * - Deal Health Analysis with AI-generated insights
 * - Explainability tooltips for metrics
 * - Strategy recommendations
 * - Executive summaries
 */
export default function AIAutopilotPanel({ deal, scenarios, onRefresh }) {
  const [healthAnalysis, setHealthAnalysis] = useState(null);
  const [strategyRec, setStrategyRec] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState({
    health: false,
    strategy: false,
    summary: false
  });
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchHealthAnalysis = async () => {
    if (!deal?.deal_id) return;
    
    setLoading(prev => ({ ...prev, health: true }));
    setError(null);
    
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/deals/${deal.deal_id}/ai/health`,
        { withCredentials: true }
      );
      setHealthAnalysis(res.data);
    } catch (err) {
      console.error("Failed to get AI health analysis:", err);
      setError("Failed to analyze deal health");
    } finally {
      setLoading(prev => ({ ...prev, health: false }));
    }
  };
  
  const fetchStrategyRecommendation = async () => {
    if (!deal?.deal_id) return;
    
    setLoading(prev => ({ ...prev, strategy: true }));
    
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/deals/${deal.deal_id}/ai/strategy`,
        { withCredentials: true }
      );
      setStrategyRec(res.data);
    } catch (err) {
      console.error("Failed to get strategy recommendation:", err);
    } finally {
      setLoading(prev => ({ ...prev, strategy: false }));
    }
  };
  
  const fetchSummary = async () => {
    if (!deal?.deal_id) return;
    
    setLoading(prev => ({ ...prev, summary: true }));
    
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/deals/${deal.deal_id}/ai/summary`,
        { withCredentials: true }
      );
      setSummary(res.data.summary);
    } catch (err) {
      console.error("Failed to get AI summary:", err);
    } finally {
      setLoading(prev => ({ ...prev, summary: false }));
    }
  };
  
  const runFullAnalysis = async () => {
    await Promise.all([
      fetchHealthAnalysis(),
      fetchStrategyRecommendation(),
      fetchSummary()
    ]);
  };
  
  const getHealthColor = (score) => {
    if (score >= 80) return "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]";
    if (score >= 60) return "text-green-400";
    if (score >= 40) return "text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]";
    return "text-red-400";
  };
  
  const getHealthBg = (score) => {
    if (score >= 80) return "bg-emerald-500/10 border-emerald-500/30";
    if (score >= 60) return "bg-green-500/10 border-green-500/30";
    if (score >= 40) return "bg-amber-500/10 border-amber-500/30";
    return "bg-red-500/10 border-red-500/30";
  };
  
  const isLoading = loading.health || loading.strategy || loading.summary;
  const hasData = healthAnalysis || strategyRec || summary;
  
  return (
    <div 
      className="bg-gradient-to-b from-black to-[#0A0A0A] border border-white/[0.08] overflow-hidden"
      data-testid="ai-autopilot-panel"
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-[#0A0A0A]/80 backdrop-blur-sm/50 transition-all duration-300"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center">
            <Brain className="w-5 h-5 text-purple-400" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-white">AI Autopilot</h3>
              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-purple-500/20 text-purple-400 rounded">
                Llama 3.3
              </span>
            </div>
            <p className="text-xs text-white/70">Deal Health • Strategy • Insights</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {healthAnalysis && (
            <span className={`text-lg font-bold ${getHealthColor(healthAnalysis.health_score)}`}>
              {healthAnalysis.health_score}/100
            </span>
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-white/70" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/70" />
          )}
        </div>
      </button>
      
      {expanded && (
        <div className="border-t border-white/[0.08]">
          {/* Action Buttons */}
          <div className="p-4 flex items-center gap-3 bg-black/50">
            <Button
              onClick={runFullAnalysis}
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold"
              data-testid="ai-analyze-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {isLoading ? "Analyzing..." : hasData ? "Refresh Analysis" : "Analyze Deal"}
            </Button>
            
            {hasData && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={runFullAnalysis}
                    disabled={isLoading}
                    className="border-white/[0.08] text-white/70 hover:text-white"
                  >
                    <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Refresh AI Analysis</TooltipContent>
              </Tooltip>
            )}
          </div>
          
          {/* Error State */}
          {error && (
            <div className="px-4 pb-4">
              <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                {error}
              </div>
            </div>
          )}
          
          {/* AI Summary */}
          {summary && (
            <div className="px-4 pb-4">
              <div className="p-3 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-4 h-4 text-blue-400" />
                  <span className="text-xs text-white/70 uppercase font-bold">Executive Summary</span>
                </div>
                <p className="text-sm text-white/70 leading-relaxed">{summary}</p>
              </div>
            </div>
          )}
          
          {/* Health Analysis */}
          {healthAnalysis && !healthAnalysis.error && (
            <div className="px-4 pb-4 space-y-4">
              {/* Health Score Card */}
              <div className={`p-4 border rounded-lg ${getHealthBg(healthAnalysis.health_score)}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className={`w-5 h-5 ${getHealthColor(healthAnalysis.health_score)}`} />
                    <span className="font-bold text-white">
                      {healthAnalysis.health_label || "Deal Health"}
                    </span>
                  </div>
                  <span className={`text-2xl font-bold font-mono ${getHealthColor(healthAnalysis.health_score)}`}>
                    {healthAnalysis.health_score}
                  </span>
                </div>
                
                {healthAnalysis.summary && (
                  <p className="text-sm text-white/70 mb-3">{healthAnalysis.summary}</p>
                )}
                
                {/* Confidence Explanation */}
                {healthAnalysis.confidence_explanation && (
                  <div className="p-2 bg-black/50 rounded text-xs text-white/70 border-l-2 border-purple-500">
                    <Lightbulb className="w-3 h-3 inline mr-1 text-purple-400" />
                    {healthAnalysis.confidence_explanation}
                  </div>
                )}
              </div>
              
              {/* Strengths */}
              {healthAnalysis.key_strengths?.length > 0 && (
                <div>
                  <h4 className="text-xs text-white/70 uppercase font-bold mb-2 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                    Key Strengths
                  </h4>
                  <div className="space-y-1">
                    {healthAnalysis.key_strengths.map((strength, idx) => (
                      <div 
                        key={idx}
                        className="flex items-start gap-2 text-sm text-white/70 p-2 bg-emerald-500/5 rounded"
                      >
                        <CheckCircle className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] flex-shrink-0 mt-0.5" />
                        {strength}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Risk Factors */}
              {healthAnalysis.risk_factors?.length > 0 && (
                <div>
                  <h4 className="text-xs text-white/70 uppercase font-bold mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]" />
                    Risk Factors
                  </h4>
                  <div className="space-y-1">
                    {healthAnalysis.risk_factors.map((risk, idx) => (
                      <div 
                        key={idx}
                        className="flex items-start gap-2 text-sm text-white/70 p-2 bg-amber-500/5 rounded"
                      >
                        <AlertTriangle className="w-4 h-4 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] flex-shrink-0 mt-0.5" />
                        {risk}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Recommendations */}
              {healthAnalysis.recommendations?.length > 0 && (
                <div>
                  <h4 className="text-xs text-white/70 uppercase font-bold mb-2 flex items-center gap-1">
                    <Target className="w-3 h-3 text-blue-400" />
                    AI Recommendations
                  </h4>
                  <div className="space-y-1">
                    {healthAnalysis.recommendations.map((rec, idx) => (
                      <div 
                        key={idx}
                        className="flex items-start gap-2 text-sm text-white/70 p-2 bg-blue-500/5 rounded"
                      >
                        <Target className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        {rec}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Strategy Recommendation */}
          {strategyRec && !strategyRec.error && (
            <div className="px-4 pb-4">
              <div className="p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                    <span className="text-xs text-white/70 uppercase font-bold">AI Strategy Recommendation</span>
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded ${
                    strategyRec.confidence === "high" 
                      ? "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]"
                      : strategyRec.confidence === "medium"
                        ? "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]"
                        : "bg-white/20/20 text-white/70"
                  }`}>
                    {strategyRec.confidence} confidence
                  </span>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2">
                  {strategyRec.recommended_strategy}
                </h3>
                
                {strategyRec.reasoning?.length > 0 && (
                  <div className="space-y-1 mb-3">
                    {strategyRec.reasoning.map((reason, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm text-white/70">
                        <span className="text-purple-400">•</span>
                        {reason}
                      </div>
                    ))}
                  </div>
                )}
                
                {strategyRec.alternative_strategy && (
                  <div className="text-xs text-white/70 pt-2 border-t border-white/[0.08]">
                    <span className="text-white/70">Alternative:</span> {strategyRec.alternative_strategy}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Empty State */}
          {!hasData && !isLoading && (
            <div className="p-8 text-center">
              <Brain className="w-12 h-12 text-white/70 mx-auto mb-3" />
              <h4 className="text-lg font-bold text-white mb-1">AI Analysis Ready</h4>
              <p className="text-sm text-white/70 mb-4">
                Get AI-powered insights on deal health, risks, and strategy recommendations.
              </p>
              <Button
                onClick={runFullAnalysis}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Analyze Deal
              </Button>
            </div>
          )}
          
          {/* Loading State */}
          {isLoading && !hasData && (
            <div className="p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
              </div>
              <h4 className="text-lg font-bold text-white mb-1">Analyzing Deal...</h4>
              <p className="text-sm text-white/70">
                AI is evaluating your deal data
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * Explainability Tooltip - Click to get AI explanation of a metric
 */
export function ExplainButton({ dealId, metricType, value, children }) {
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  
  const fetchExplanation = async () => {
    if (explanation) {
      setOpen(!open);
      return;
    }
    
    setLoading(true);
    setOpen(true);
    
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/deals/${dealId}/ai/explain`,
        { metric_type: metricType, value },
        { withCredentials: true }
      );
      setExplanation(res.data.explanation);
    } catch (err) {
      setExplanation("Unable to get AI explanation");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          onClick={fetchExplanation}
          className="inline-flex items-center gap-1 text-white/70 hover:text-purple-400 transition-all duration-300"
        >
          {children}
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent 
        side="bottom" 
        className="max-w-sm bg-black border-white/[0.08] p-3"
      >
        {loading ? (
          <div className="flex items-center gap-2 text-white/70">
            <Loader2 className="w-4 h-4 animate-spin" />
            Getting AI explanation...
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-1 text-xs text-purple-400 font-bold uppercase">
              <Brain className="w-3 h-3" />
              AI Explanation
            </div>
            <p className="text-sm text-white/70">{explanation}</p>
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
